import { getResolvedPostFlightStatus } from '../booking/activeFlightSelection';
import { getBookingEndDate } from '../booking/bookingUtils';

export function getMovementTotalFromLegs(tech = {}) {
  const legs = Array.isArray(tech?.legs) ? tech.legs : [];
  const totalFromLegs = legs.reduce((sum, leg) => {
    const delta = Number(leg?.hobbsDelta);
    return Number.isFinite(delta) && delta > 0 ? sum + delta : sum;
  }, 0);

  return Number(totalFromLegs.toFixed(2));
}

export function getBillableHours({ booking, techLogEntry = {}, billingBasis }) {
  const basis = String(billingBasis || 'hobbs').trim().toLowerCase();

  if (basis === 'hobbs') {
    const movementFromLegs = getMovementTotalFromLegs(techLogEntry);
    if (movementFromLegs > 0) {
      return { hours: movementFromLegs, available: true };
    }

    const hobbsStart = Number(
      techLogEntry?.hobbsStart ?? techLogEntry?.startReading
    );
    const hobbsEnd = Number(
      techLogEntry?.hobbsEnd ?? techLogEntry?.endReading
    );
    if (Number.isFinite(hobbsStart) && Number.isFinite(hobbsEnd)) {
      const hobbsDelta = hobbsEnd - hobbsStart;
      return {
        hours: hobbsDelta >= 0 ? Number(hobbsDelta.toFixed(2)) : 0,
        available: hobbsDelta >= 0,
      };
    }

    return { hours: 0, available: false };
  }

  if (basis === 'flight') {
    const flightTime = Number(techLogEntry?.flightTime);
    return {
      hours: Number.isFinite(flightTime) && flightTime > 0 ? flightTime : 0,
      available: Number.isFinite(flightTime),
    };
  }

  const durationMinutes = Number(booking?.duration);
  return {
    hours: Number.isFinite(durationMinutes) && durationMinutes > 0
      ? durationMinutes / 60
      : 0,
    available: Number.isFinite(durationMinutes),
  };
}

export function calculatePaymentTotals({
  booking,
  techLogEntry = {},
  billingBasis,
  hourlyRate,
  landingFeeEnabled,
  landingFee,
}) {
  const billable = getBillableHours({ booking, techLogEntry, billingBasis });
  const rate = Number(hourlyRate);
  const fee = Number(landingFee);
  const hasRate = Number.isFinite(rate);
  const hasFee = !landingFeeEnabled || Number.isFinite(fee);
  const total = hasRate && hasFee
    ? billable.hours * rate + (landingFeeEnabled ? fee : 0)
    : NaN;

  return {
    hours: billable.hours,
    flightCharge: hasRate ? billable.hours * rate : NaN,
    landingFee: landingFeeEnabled && Number.isFinite(fee) ? fee : 0,
    total,
    amountAvailable: billable.available && hasRate && hasFee && Number.isFinite(total),
  };
}

export function isPaymentComplete(booking) {
  const status = String(booking?.payment_status || '').trim().toLowerCase();
  return status === 'paid' || status === 'not_required';
}

export function isTechLogComplete(booking, techLogEntry = {}) {
  const hasOfficialEntry =
    techLogEntry &&
    typeof techLogEntry === 'object' &&
    Object.keys(techLogEntry).length > 0;

  return hasOfficialEntry || Boolean(getResolvedPostFlightStatus(booking));
}

export function isOutstandingPaymentBooking(
  booking,
  techLogEntry = {},
  now = new Date()
) {
  const end = getBookingEndDate(booking);

  return Boolean(
    end &&
    end <= now &&
    isTechLogComplete(booking, techLogEntry) &&
    !isPaymentComplete(booking)
  );
}
